import {
  formatAmountWithUnit,
  GIFT_CODE_BATCH_MAX,
  GIFT_CODE_MAX_AMOUNT_CENTS,
  GIFT_CODE_TYPE_LABELS,
  GIFT_CODE_TYPES,
  type GiftCodeType,
  parseAmount,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Calendar } from "@loot-os/ui/components/calendar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@loot-os/ui/components/select";
import { toast } from "@loot-os/ui/components/sonner";
import { Textarea } from "@loot-os/ui/components/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { CalendarIcon, Plus } from "lucide-react";
import { useState } from "react";

import { amountInputLabel, amountInputStep, amountInputValue, useCurrency } from "../lib/currency";
import { formatShortDate } from "../lib/format";
import { endOfLocalDay } from "../lib/giftCodes";
import { useTRPC } from "../lib/trpc";

/** The form that generates a batch: type, amount in the store's currency, how many, an expiry, a note. */
export function GenerateGiftCodesDialog() {
  const trpc = useTRPC();
  const currency = useCurrency();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tenantId } = useParams({ from: "/app/tenants/$tenantId" });
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<GiftCodeType>("giveaway");
  const [amount, setAmount] = useState("");
  const [count, setCount] = useState("");
  const [expiresOn, setExpiresOn] = useState<Date | undefined>();
  const [pickingExpiry, setPickingExpiry] = useState(false);
  const [note, setNote] = useState("");

  const reset = () => {
    setType("giveaway");
    setAmount("");
    setCount("");
    setExpiresOn(undefined);
    setNote("");
  };

  const generate = useMutation(
    trpc.giftCodes.generate.mutationOptions({
      onSuccess: (batch) => {
        toast.success(`Generated ${batch.codeCount} ${batch.codeCount === 1 ? "code" : "codes"}.`);
        setOpen(false);
        reset();
        void queryClient.invalidateQueries({ queryKey: trpc.giftCodes.pathKey() });
        void navigate({
          to: "/tenants/$tenantId/storefront/gift-codes/$batchId",
          params: { tenantId, batchId: batch.id },
        });
      },
    }),
  );

  const amountCents = parseAmount(amount, currency);
  const codes = /^\d+$/.test(count) ? Number(count) : null;
  const amountValid =
    amountCents !== null && amountCents >= 1 && amountCents <= GIFT_CODE_MAX_AMOUNT_CENTS;
  const countValid = codes !== null && codes >= 1 && codes <= GIFT_CODE_BATCH_MAX;
  const valid = amountValid && countValid;

  const submit = () => {
    if (!valid || amountCents === null || codes === null) return;
    generate.mutate({
      type,
      amountCents,
      count: codes,
      expiresAt: expiresOn ? endOfLocalDay(expiresOn).toISOString() : null,
      note: note.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger render={<Button type="button" />}>
        <Plus />
        Generate Codes
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate gift codes</DialogTitle>
          <DialogDescription>
            Each code adds its amount to the balance of the shopper who redeems it, once, in this
            environment. You can download the codes from the batch page afterwards.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="gift-code-type">Type</Label>
            <Select
              items={GIFT_CODE_TYPE_LABELS}
              value={type}
              onValueChange={(value) => setType(value as GiftCodeType)}
            >
              <SelectTrigger id="gift-code-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GIFT_CODE_TYPES.map((option) => (
                  <SelectItem key={option} value={option}>
                    {GIFT_CODE_TYPE_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="gift-code-amount">
                {amountInputLabel("Amount per code", currency)}
              </Label>
              <Input
                id="gift-code-amount"
                type="number"
                min={amountInputStep(currency)}
                max={amountInputValue(GIFT_CODE_MAX_AMOUNT_CENTS, currency)}
                step={amountInputStep(currency)}
                placeholder={amountInputValue(500, currency)}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="gift-code-count">Number of codes</Label>
              <Input
                id="gift-code-count"
                type="number"
                min={1}
                max={GIFT_CODE_BATCH_MAX}
                step={1}
                placeholder="100"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="gift-code-expiry">Expires</Label>
            <Popover open={pickingExpiry} onOpenChange={setPickingExpiry}>
              <PopoverTrigger
                render={
                  <Button
                    id="gift-code-expiry"
                    type="button"
                    variant="outline"
                    className="w-full justify-start px-2.5 font-normal"
                  />
                }
              >
                <CalendarIcon data-icon="inline-start" className="text-muted-foreground" />
                {expiresOn ? (
                  `End of ${formatShortDate(expiresOn)}`
                ) : (
                  <span className="text-muted-foreground">Never</span>
                )}
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  defaultMonth={expiresOn ?? new Date()}
                  selected={expiresOn}
                  disabled={{ before: new Date() }}
                  onSelect={(day) => {
                    setExpiresOn(day);
                    setPickingExpiry(false);
                  }}
                />
                {expiresOn && (
                  <div className="flex justify-end border-t px-3 py-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setExpiresOn(undefined);
                        setPickingExpiry(false);
                      }}
                    >
                      Never expire
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="gift-code-note">Note</Label>
            <Textarea
              id="gift-code-note"
              maxLength={500}
              rows={2}
              placeholder="Who the codes are for, the campaign"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {valid && amountCents !== null && codes !== null
              ? `${codes} ${codes === 1 ? "code" : "codes"} worth ${formatAmountWithUnit(amountCents, currency)} each, ${formatAmountWithUnit(amountCents * codes, currency)} in all.`
              : `Up to ${GIFT_CODE_BATCH_MAX.toLocaleString()} codes a batch, each worth at most ${formatAmountWithUnit(GIFT_CODE_MAX_AMOUNT_CENTS, currency)}.`}
          </p>
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={generate.isPending || !valid}>
              {generate.isPending ? "Generating…" : "Generate Codes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
